'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Image from 'next/image';
import { usePathname } from 'next/navigation';

import VerifiedTick from '@/assets/icons/verified-tick.svg';
import LeftArrow from '@/assets/images/arrow-left.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import Button from '@/components/V2/Button';
import ButtonLink from '@/components/V2/ButtonLink';
import Checkbox from '@/components/V2/Checkbox';
import Spinner from '@/components/V2/PetProfile/Spinner';
import AuthToast from '@/components/V2/Popup/AuthToast';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useAppDispatch } from '@/store';
import { customerApi } from '@/store/api/customer';
import {
  useEnable2FaMutation,
  useLogin2FAMutation,
  useMarkDeviceAsTrustedMutation,
  useResendCodeMutation,
} from '@/store/api/twoFactorAuthentication';
import { setAuthenticationStatus } from '@/store/slices/authentication';

interface EnterCodeProps {
  onVerify?: (recoveryCodes: string[]) => void;
  onBack?: () => void;
  onResend?: () => void;
  inPage?: boolean;
  codeData?: { smsSendTo: string; emailSendTo: string; expiry: string };
}

type ValidationState = 'idle' | 'success' | 'error';

enum Status {
  Correct = 0,
  Expired = 1,
  Incorrect = 2,
}

const ERROR_MESSAGES = {
  FAILED_TO_VERIFY: 'Failed to verify code',
  EXPIRED_CODE: 'The security code is no longer valid. Click "Resend" to get a new code.',
  INCORRECT_CODE: 'The code you entered is incorrect. Please try again.',
} as const;

const EnterCode: React.FC<EnterCodeProps> = ({
  onVerify = () => {},
  onBack = () => {},
  onResend = () => {},
  inPage = false,
  codeData,
}) => {
  const { customer } = useAuthContext();
  const pathname = usePathname();
  const isEnter2FA =
    typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get('enter2FA') === 'true' ||
      new URLSearchParams(window.location.search).get('modal') === 'enter2FA');
  const pending2FaCustomerId =
    isEnter2FA && typeof window !== 'undefined'
      ? Number(localStorage.getItem('__pending_2fa_customer_id') || '') || undefined
      : undefined;

  const dispatch = useAppDispatch();
  const [values, setValues] = useState<string[]>(['', '', '', '', '', '']);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [enable2Fa, { isLoading: isVerifying }] = useEnable2FaMutation();
  const [resendCode, { isLoading: isResending }] = useResendCodeMutation();
  const [login2FA, { isLoading: isLoggingIn2FA }] = useLogin2FAMutation();
  const [markDeviceAsTrusted, { isLoading: isMarkingTrusted }] = useMarkDeviceAsTrustedMutation();
  const [trustDevice, setTrustDevice] = useState(false);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      const digitsOnly = value.replace(/\D/g, '');

      if (digitsOnly.length === 0) {
        const newValues = [...values];
        newValues[index] = '';
        setValues(newValues);
        return;
      }

      if (digitsOnly.length === 1) {
        const newValues = [...values];
        newValues[index] = digitsOnly;
        setValues(newValues);

        if (index < 5) {
          inputRefs.current[index + 1]?.focus();
        }
      } else {
        const newValues = [...values];
        const remainingSlots = 6 - index;
        const digitsToUse = digitsOnly.slice(0, remainingSlots);

        for (let i = 0; i < digitsToUse.length; i++) {
          if (index + i < 6) {
            newValues[index + i] = digitsToUse[i];
          }
        }

        setValues(newValues);

        const nextIndex = Math.min(index + digitsToUse.length, 5);
        setTimeout(() => {
          inputRefs.current[nextIndex]?.focus();
        }, 0);
      }
    },
    [values]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !values[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [values]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

      if (pastedData.length > 0) {
        const newValues = [...values];
        const pastedDigits = pastedData.slice(0, 6);

        const startIndex = focusedIndex !== null ? focusedIndex : 0;

        for (let i = 0; i < pastedDigits.length && startIndex + i < 6; i++) {
          newValues[startIndex + i] = pastedDigits[i];
        }

        setValues(newValues);

        const nextEmptyIndex = newValues.findIndex((val, idx) => idx > startIndex && !val);
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(startIndex + pastedDigits.length, 5);

        setTimeout(() => {
          inputRefs.current[focusIndex]?.focus();
        }, 0);
      }
    },
    [values, focusedIndex]
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleVerify = useCallback(async () => {
    const verifyCustomerId = customer?.customerID || pending2FaCustomerId;
    if (!verifyCustomerId) {
      toast(
        ({ closeToast }) => (
          <AuthToast type="error" message="Customer information not available" closeToast={closeToast} />
        ),
        { containerId: AUTH_MODAL_TOAST_CONTAINER }
      );
      return;
    }

    const code = values.join('');
    if (code.length !== 6) {
      toast(
        ({ closeToast }) => (
          <AuthToast type="error" message="Please enter the security code." closeToast={closeToast} />
        ),
        { containerId: AUTH_MODAL_TOAST_CONTAINER }
      );
      return;
    }

    setIsLoading(true);
    setValidationState('idle');

    const isOnLoginPage = pathname.includes('/login');

    try {
      if (isOnLoginPage) {
        const rememberMe =
          (typeof window !== 'undefined' && localStorage.getItem('__pending_2fa_remember_me') === 'true') || false;
        const loginResp = await login2FA({ customerId: verifyCustomerId, code, rememberMe }).unwrap();
        if (loginResp.success && loginResp.data?.status === Status.Correct) {
          // Set flag BEFORE RTK Query invalidation to prevent modal from reopening
          if (typeof window !== 'undefined') {
            localStorage.setItem('__2fa_flow_completing', 'true');
          }
          try {
            dispatch(customerApi.util.invalidateTags(['Customers']));
            await dispatch(customerApi.endpoints.getCustomer.initiate(undefined, { forceRefetch: true }));
            dispatch(setAuthenticationStatus(true));
            if (typeof window !== 'undefined') {
              localStorage.removeItem('__pending_2fa_customer_id');
              localStorage.removeItem('__pending_2fa_remember_me');
            }
          } catch (e) {}
          setValidationState('success');
          // If user checked "Trust this device", call the trust device API
          if (trustDevice) {
            try {
              await markDeviceAsTrusted().unwrap();
              toast(
                ({ closeToast }) => (
                  <AuthToast type="success" message="Device marked as trusted" closeToast={closeToast} />
                ),
                { containerId: AUTH_MODAL_TOAST_CONTAINER }
              );
            } catch (trustError: any) {
              // Show error but don't block the flow
              const trustErrorMessage =
                trustError?.data?.message || trustError?.data?.exceptionMessage || 'Failed to mark device as trusted';
              toast(
                ({ closeToast }) => <AuthToast type="error" message={trustErrorMessage} closeToast={closeToast} />,
                {
                  containerId: AUTH_MODAL_TOAST_CONTAINER,
                }
              );
            }
          }
          onVerify([]);
          return;
        } else {
          // Handle error cases for login2FA with new status
          setValidationState('error');
          let errorMessage: string = ERROR_MESSAGES.FAILED_TO_VERIFY;

          if (loginResp.data?.status === Status.Expired) {
            errorMessage = ERROR_MESSAGES.EXPIRED_CODE;
          } else if (loginResp.data?.status === Status.Incorrect) {
            errorMessage = ERROR_MESSAGES.INCORRECT_CODE;
          } else {
            errorMessage = loginResp.message || ERROR_MESSAGES.FAILED_TO_VERIFY;
          }

          toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
            containerId: AUTH_MODAL_TOAST_CONTAINER,
          });
          setTimeout(() => setValidationState('idle'), 3000);
          return;
        }
      } else {
        // Signup flow - call enable2FA API
        const response = await enable2Fa({
          customerId: verifyCustomerId,
          code,
          method: 1,
        }).unwrap();

        if (response.success && response.data?.status === Status.Correct) {
          // Set flag BEFORE RTK Query invalidation to prevent modal from reopening
          if (typeof window !== 'undefined') {
            localStorage.setItem('__2fa_flow_completing', 'true');
          }
          setValidationState('success');
          try {
            // dispatch(customerApi.util.invalidateTags(['Customers']));
            // await dispatch(customerApi.endpoints.getCustomer.initiate(undefined, { forceRefetch: true }));
            dispatch(setAuthenticationStatus(true));
            if (typeof window !== 'undefined') localStorage.removeItem('__pending_2fa_customer_id');
          } catch (e) {}
          // If user checked "Trust this device", call the trust device API
          if (trustDevice) {
            try {
              await markDeviceAsTrusted().unwrap();
              toast(
                ({ closeToast }) => (
                  <AuthToast type="success" message="Device marked as trusted" closeToast={closeToast} />
                ),
                { containerId: AUTH_MODAL_TOAST_CONTAINER }
              );
            } catch (trustError: any) {
              // Show error but don't block the flow
              const trustErrorMessage =
                trustError?.data?.message || trustError?.data?.exceptionMessage || 'Failed to mark device as trusted';
              toast(
                ({ closeToast }) => <AuthToast type="error" message={trustErrorMessage} closeToast={closeToast} />,
                {
                  containerId: AUTH_MODAL_TOAST_CONTAINER,
                }
              );
            }
          }
          setTimeout(() => onVerify(response.data.recoveryCodes), 1000);
        } else {
          setValidationState('error');
          let errorMessage: string = ERROR_MESSAGES.FAILED_TO_VERIFY;

          if (response.data?.status === Status.Expired) {
            errorMessage = ERROR_MESSAGES.EXPIRED_CODE;
          } else if (response.data?.status === Status.Incorrect) {
            errorMessage = ERROR_MESSAGES.INCORRECT_CODE;
          } else {
            errorMessage = response.message || ERROR_MESSAGES.FAILED_TO_VERIFY;
          }

          toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
            containerId: AUTH_MODAL_TOAST_CONTAINER,
          });
          setTimeout(() => setValidationState('idle'), 3000);
        }
      }
    } catch (error: any) {
      setValidationState('error');
      const errorMessage =
        error?.data?.message || error?.data?.exceptionMessage || 'The code you entered is incorrect. Please try again.';
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
      setTimeout(() => setValidationState('idle'), 3000);
    } finally {
      setIsLoading(false);
    }
  }, [
    customer?.customerID,
    pending2FaCustomerId,
    values,
    pathname,
    login2FA,
    trustDevice,
    onVerify,
    dispatch,
    markDeviceAsTrusted,
    enable2Fa,
  ]);

  const handleResend = useCallback(async () => {
    const resendCustomerId = customer?.customerID || pending2FaCustomerId;

    if (!resendCustomerId || !canResend) return;

    try {
      const response = await resendCode({
        customerId: resendCustomerId,
        method: 1,
      }).unwrap();

      if (response.success && response.data) {
        setTimeLeft(60);
        setCanResend(false);
        setValues(['', '', '', '', '', '']);
        setValidationState('idle');
        onResend();
        toast(
          ({ closeToast }) => <AuthToast type="success" message="New code sent successfully" closeToast={closeToast} />,
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
      } else {
        toast(
          ({ closeToast }) => (
            <AuthToast type="error" message={response.message || 'Failed to resend code'} closeToast={closeToast} />
          ),
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.data?.exceptionMessage || 'Failed to resend code';
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
    }
  }, [customer, canResend, resendCode, onResend, pending2FaCustomerId]);

  const handleBackClick = useCallback(() => {
    // Check if we're on a login page (any path that includes "/login")
    const isOnLoginPage = pathname.includes('/login');

    if (isOnLoginPage) {
      // For login page case, remove the modal parameter and close modal
      try {
        // Mark that user returned from 2FA so login form can reset the button UI
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('__2fa_reset_login_verified', 'true');
          } catch (e) {}
        }
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('modal') === 'enter2FA') {
          urlParams.delete('modal');
        } else if (urlParams.get('enter2FA') === 'true') {
          urlParams.delete('enter2FA');
        }
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.replaceState({}, '', newUrl);
      } catch (e) {
        console.error('Failed to update URL:', e);
      }
    } else {
      // For normal case (not on login page), call the onBack prop
      onBack();
    }
  }, [pathname, onBack]);

  const getBoxClasses = (index: number) => {
    const value = values[index];
    const isFocused = focusedIndex === index;
    const isEmpty = !value;

    const WHITE_BG = 'v2-bg-white';

    let borderClasses = '';
    let bgClasses = '';

    if (validationState === 'success') {
      borderClasses = 'v2-border-[#101828] v2-border-2';
      bgClasses = 'v2-bg-[#E2E8E6]';
    } else if (validationState === 'error') {
      borderClasses = 'v2-border-[#D92D20] v2-border-2';
      bgClasses = 'v2-bg-[#FEF3F2]';
    } else if (isFocused || !isEmpty) {
      borderClasses = 'v2-border-[#101828] v2-border-2';
      bgClasses = WHITE_BG;
    } else {
      borderClasses = 'v2-border-[#D0D5DD] v2-border';
      bgClasses = WHITE_BG;
    }

    return `v2-w-[45px] md:v2-w-[80px] v2-h-[48px] md:v2-h-[80px] v2-rounded-xs v2-p-2 v2-flex v2-items-center v2-justify-center ${borderClasses} ${bgClasses}`;
  };

  const getInputClasses = (index: number) => {
    const value = values[index];
    const isEmpty = !value;

    let textColorClasses = '';

    if (validationState === 'success') {
      textColorClasses = 'v2-text-[#101828]';
    } else if (validationState === 'error') {
      textColorClasses = 'v2-text-[#D92D20]';
    } else if (!isEmpty) {
      textColorClasses = 'v2-text-[#101828]';
    } else {
      textColorClasses = 'v2-text-[#D0D5DD]';
    }

    return `v2-w-[30px] md:v2-w-[64px] v2-h-8 md:v2-h-[64px] v2-text-center v2-border-0 v2-outline-none v2-bg-transparent v2-p-0 v2-font-sweet-sans-pro !v2-text-[24px] md:!v2-text-[48px] !v2-font-normal !v2-leading-[64px] !v2-tracking-[-0.96px] placeholder:v2-text-[#D0D5DD] ${textColorClasses}`;
  };

  const getButtonContent = () => {
    if (validationState === 'success') {
      return (
        <div className="v2-flex v2-items-center v2-gap-2">
          <span className="v2-font-sweet-sans-pro-medium !v2-font-medium">Verified</span>
          <Image src={VerifiedTick} alt="" className="v2-w-5 v2-h-5" />
        </div>
      );
    }
    if (isLoading || isVerifying)
      return (
        <div className="v2-flex v2-items-center v2-gap-2">
          <span className="v2-font-sweet-sans-pro-medium !v2-font-medium">Verifying</span>
          <Spinner height={20} />
        </div>
      );
    return <span className="v2-font-sweet-sans-pro-medium !v2-font-medium">Verify</span>;
  };

  const getButtonClasses = () => {
    if (validationState === 'success') {
      return 'v2-bg-[#12B76A] hover:v2-bg-[#0F9A5C]';
    }
    if (validationState === 'error') {
      return 'v2-bg-[#F04438] hover:v2-bg-[#D92D20]';
    }
    return 'v2-bg-[#164634] hover:v2-bg-[#0F2A20]';
  };

  return (
    <div>
      <ButtonLink
        scheme={'primary'}
        variant={'outlined'}
        className={`!v2-border-0 !v2-p-0 !v2-rounded-0 v2-font-sweet-sans-pro-bold v2-font-bold !v2-text-lg md:!v2-text-lg !v2-w-fit v2-flex v2-items-center !v2-leading-[28px] v2-gap-3 ${
          inPage ? 'v2-mb-[32px]' : 'v2-mb-8 md:v2-mb-[34px]'
        }`}
        onClick={handleBackClick}
      >
        <Image src={LeftArrow} alt="" width={24} height={24} />
        Back
      </ButtonLink>

      <h2 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-lg md:v2-text-4xl v2-text-[#101828] v2-mb-2 md:v2-mb-2">
        Two-Factor Authentication
      </h2>
      <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-lg v2-text-[#101828] v2-mb-8">
        Enter your verification code sent to your (phone / email)
      </p>

      <div
        className={`v2-flex v2-items-center v2-justify-between md:v2-justify-start v2-gap-2 md:v2-gap-3 ${
          inPage ? 'v2-mb-[58px]' : '!v2-mb-[18px] !md:v2-mb-[18px]'
        }`}
      >
        {[0, 1, 2].map(index => (
          <div key={index} className={getBoxClasses(index)}>
            <input
              ref={el => (inputRefs.current[index] = el)}
              type="text"
              maxLength={1}
              value={values[index]}
              placeholder="0"
              className={getInputClasses(index)}
              style={{ caretColor: 'transparent' }}
              onChange={e => handleInputChange(index, e.target.value)}
              onFocus={e => {
                setFocusedIndex(index);
                e.target.select();
              }}
              onBlur={() => setFocusedIndex(null)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
            />
          </div>
        ))}
        <div className="v2-w-[26px] v2-hidden md:v2-flex v2-items-center v2-justify-center">
          <p className="!v2-text-[48px] !v2-font-normal v2-text-[#D0D5DD] v2-leading-none v2-font-sweet-sans-pro !v2-tracking-[-0.96px]">
            -
          </p>
        </div>
        {[3, 4, 5].map(index => (
          <div key={index} className={getBoxClasses(index)}>
            <input
              ref={el => (inputRefs.current[index] = el)}
              type="text"
              maxLength={1}
              value={values[index]}
              placeholder="0"
              className={getInputClasses(index)}
              style={{ caretColor: 'transparent' }}
              onChange={e => handleInputChange(index, e.target.value)}
              onFocus={e => {
                setFocusedIndex(index);
                e.target.select();
              }}
              onBlur={() => setFocusedIndex(null)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
            />
          </div>
        ))}
      </div>

      <div className="!v2-mb-[18px] !md:v2-mb-[18px]">
        <Checkbox
          inputClassName="!v2-min-h-[20px] !v2-w-5 !v2-min-w-[20px]"
          id="trust-device"
          name="trustDevice"
          value={trustDevice}
          onChange={setTrustDevice}
        >
          <span className="v2-font-sweet-sans-pro v2-font-normal v2-text-lg v2-text-[#101828]">Trust this device</span>
        </Checkbox>
      </div>

      <Button
        scheme="primary"
        variant="filled"
        btnSize="md"
        type="button"
        onClick={handleVerify}
        disabled={values.some(v => !v) || isLoading || isVerifying || isMarkingTrusted}
        className={`v2-w-full v2-h-[44px] v2-text-white !v2-font-sweet-sans-pro-medium v2-text-base v2-rounded-lg v2-border-0 v2-mb-4 v2-flex v2-items-center v2-justify-center v2-gap-2 v2-transition-colors !v2-font-medium ${getButtonClasses()}`}
      >
        {getButtonContent()}
      </Button>

      <p className="v2-text-[#344054] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-text-center v2-hidden md:v2-block">
        Didn&apos;t receive a code?{' '}
        <button
          onClick={handleResend}
          disabled={!canResend || isResending}
          className={`v2-font-sweet-sans-pro-bold v2-font-bold v2-underline v2-bg-transparent v2-border-0 v2-p-0 ${
            canResend && !isResending
              ? 'v2-text-[#475467] v2-cursor-pointer'
              : 'v2-text-[#475467] v2-cursor-not-allowed'
          }`}
        >
          {isResending ? 'Sending...' : 'Resend'}
        </button>{' '}
        {!canResend && `in ${formatTime(timeLeft)}`}
      </p>
      <p className="v2-text-[#344054] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-text-center md:v2-hidden v2-block">
        Didn&apos;t receive a code?
      </p>
      <p className="v2-text-[#344054] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-text-center md:v2-hidden v2-block">
        <button
          onClick={handleResend}
          disabled={!canResend || isResending}
          className={`v2-font-sweet-sans-pro-bold v2-font-bold v2-bg-transparent v2-border-0 v2-p-0 ${
            canResend && !isResending
              ? 'v2-text-[#475467] v2-cursor-pointer'
              : 'v2-text-[#D0D5DD] v2-cursor-not-allowed'
          }`}
        >
          {isResending ? 'Sending...' : 'Resend'}
        </button>{' '}
        {!canResend && `in ${formatTime(timeLeft)}`}
      </p>
    </div>
  );
};

export default EnterCode;
